// @ts-check
import { createRouter, createWebHistory } from 'vue-router';

/**
 * Wraps a dynamic import with retry logic for ChunkLoadError.
 * On failure (stale deploy), forces a full page reload.
 * @param {() => Promise<any>} componentImport
 * @returns {() => Promise<any>}
 */
function lazyLoad (componentImport) {
  return () =>
    componentImport().catch((error) => {
      if (
        error.name === 'ChunkLoadError' ||
        error.message?.includes('Loading chunk') ||
        error.message?.includes('Loading CSS chunk')
      ) {
        window.location.reload();
        return new Promise(() => {});
      }
      throw error;
    });
}

const POST_LOGIN_REDIRECT_KEY = 'postLoginRedirect'
const CHAT_INTERACTIVE_PATH = '/chatInteractive'

function isChatInteractivePath (path) {
  return typeof path === 'string' && path.toLowerCase() === CHAT_INTERACTIVE_PATH.toLowerCase()
}

// Función para redirigir según la intención
function redirectIntent (to, next) {
  try {
    // Si intentó entrar al chat sin sesión, recordar destino para post-login (Mercados / CHAT_PRIORITY_RESTRICTION).
    // Acepta /chatInteractive y /chatinteractive (case-insensitive).
    if (isChatInteractivePath(to.path)) {
      try {
        sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, to.fullPath)
      } catch (e) { /* ignore */ }
    }

    if (to.path && to.path.includes('/intent/')) {
      if (to.params.id && !isNaN(to.params.id)) {
        next({
          path: '/login',
          query: { searchIntentId: to.params.id } // ej: /login?redirect=/dashboard
        })
      } else {
        next({ path: '/login' });
      }
    } else {
      next({ path: '/login' });
    }
  } catch (error) {
    next({ path: '/login' });
  }
}

// Guard de ruta para verificar si el usuario está firmado
function isSignInGuard (to, from, next) {
  if (!localStorage.getItem('k-chat-token')) {
    redirectIntent(to, next);
  } else {
    next();
  }
}

function handoffDeniedCode (err) {
  return err && err.response && err.response.data && err.response.data.code
    ? err.response.data.code
    : 'handoff_required'
}

// Usuarios EuGenIA (agent=true) solo entran con JWT de derivación,
// chat activo o perfil help desk. El resto (OpenChatButton) sigue igual.
async function ensureHandoffAccess (to) {
  if (localStorage.getItem('agent') !== 'true') {
    return true
  }
  const { default: chatsService } = await import('./shared/services/chats.service')
  const token = typeof to.query.handoff === 'string' ? to.query.handoff : ''
  return new Promise((resolve) => {
    chatsService.validateHandoff(token, (err, result) => {
      if (!err && result && result.allowed) {
        // Un rechazo anterior sin consumir no debe avisarse sobre un ingreso válido.
        sessionStorage.removeItem('handoffDenied')
        if (token && result.reason !== 'ticket' && to.query.handoff) {
          const query = { ...to.query }
          delete query.handoff
          resolve({ path: to.path, query, hash: to.hash, replace: true })
          return
        }
        resolve(true)
        return
      }
      sessionStorage.setItem('handoffDenied', handoffDeniedCode(err))
      resolve({ path: '/home' })
    })
  })
}

// Crear el enrutador
const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),

  scrollBehavior: () => ({ left: 0, top: 0 }),

  routes: [
    { path: '', redirect: '/home' },

    {
      path: '',
      component: lazyLoad(() => import('./layouts/sidebar/Sidebar.vue')),
      beforeEnter: isSignInGuard,
      children: [
        { path: '/home', component: lazyLoad(() => import('./views/home/Home.vue')) },
        { path: '/intent/:id', component: lazyLoad(() => import('./views/intent/Intent.vue')) },
        { path: '/search', component: lazyLoad(() => import('./views/search/Search.vue')) },
        { path: '/favorites', component: lazyLoad(() => import('./views/favorites/Favorites.vue')) },
        { path: '/chatInteractive', alias: '/chatinteractive', component: lazyLoad(() => import('./views/chatInteractive/Chat.vue')), beforeEnter: ensureHandoffAccess },
        { path: '/feedback/:chatId/:ratingId', component: lazyLoad(() => import('./views/feedback/FeedBack.vue')) },
        { path: '/new-articles', component: lazyLoad(() => import('./views/new-articles/NewArticles.vue')) },
      ], 
    },

    /// full screen layouts
    { path: '/login', component: lazyLoad(() => import('./views/login/Login.vue')) },
    { path: '/pages/error-404', component: lazyLoad(() => import('./views/pages/Error404.vue')) },
  ],
});

router.afterEach(() => {
  // Remove initial loading
  const appLoading = document.getElementById('loading-bg');
  if (appLoading) {
    appLoading.style.display = 'none';
  }
});

// Handle navigation failures caused by chunk load errors (stale deploys)
router.onError((error) => {
  if (
    error.name === 'ChunkLoadError' ||
    error.message?.includes('Loading chunk') ||
    error.message?.includes('Loading CSS chunk')
  ) {
    window.location.reload();
  }
});

export default router;
